// src/components/PitchRenderer.jsx
import React, { useEffect, useState } from 'react';
import { X, Pencil, Check, XCircle } from 'lucide-react';

// Helper function to determine if a color is light
const isLightColor = (color) => {
  if (!color) return true;
  const hex = color.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  const brightness = ((r * 299) + (g * 587) + (b * 114)) / 1000;
  return brightness > 155;
};

// Get sections based on pitch configuration
const getSectionsForPitch = (config) => {
  if (!config) return ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];
  
  // Check if config has sectionsCount property
  if (config.sectionsCount) {
    if (config.sectionsCount === 8) return ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];
    if (config.sectionsCount === 4) return ['A', 'B', 'C', 'D'];
    if (config.sectionsCount === 2) return ['A', 'C'];
    if (config.sectionsCount === 1) return ['A'];
  }
  
  // Check for sectionLayout property
  if (config.sectionLayout) {
    if (config.sectionLayout === 'FULL' || config.sectionLayout === 'EIGHTH') return ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];
    if (config.sectionLayout === 'HALF') return ['A', 'B', 'C', 'D'];
    if (config.sectionLayout === 'QUARTER') return ['A', 'C'];
    if (config.sectionLayout === 'SMALL') return ['A'];
  }
  
  // Default fallback
  return ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];
};

// Get grid layout based on section configuration
const getGridLayout = (config) => {
  if (!config) return { columns: '1fr 1fr', rows: 'repeat(4, 1fr)' };
  
  // Check for sectionsCount property
  if (config.sectionsCount) {
    if (config.sectionsCount === 8) return { columns: '1fr 1fr', rows: 'repeat(4, 1fr)' };
    if (config.sectionsCount === 4) return { columns: '1fr 1fr', rows: '1fr 1fr' };
    if (config.sectionsCount === 2) return { columns: '1fr', rows: '1fr 1fr' }; // Vertical layout for quarter pitch
    if (config.sectionsCount === 1) return { columns: '1fr', rows: '1fr' };
  }
  
  // Check for sectionLayout property
  if (config.sectionLayout) {
    if (config.sectionLayout === 'FULL' || config.sectionLayout === 'EIGHTH') return { columns: '1fr 1fr', rows: 'repeat(4, 1fr)' };
    if (config.sectionLayout === 'HALF') return { columns: '1fr 1fr', rows: '1fr 1fr' };
    if (config.sectionLayout === 'QUARTER') return { columns: '1fr', rows: '1fr 1fr' }; // Vertical layout for quarter pitch
    if (config.sectionLayout === 'SMALL') return { columns: '1fr', rows: '1fr' };
  }
  
  // Default fallback
  return { columns: '1fr 1fr', rows: 'repeat(4, 1fr)' };
};

// Field markings renderers
const renderFullPitchMarkings = () => (
  <>
    <div style={{
      position: 'absolute',
      top: '2px',
      left: '2px',
      right: '2px',
      bottom: '2px',
      border: '2px solid white',
      borderRadius: '4px'
    }}></div>
    
    <div style={{
      position: 'absolute',
      left: '2px',
      right: '2px',
      top: '50%',
      height: '2px',
      transform: 'translateY(-50%)',
      backgroundColor: 'white'
    }}></div>
    
    <div style={{
      position: 'absolute',
      left: '50%',
      top: '50%',
      width: '80px',
      height: '80px',
      border: '2px solid white',
      borderRadius: '50%',
      transform: 'translate(-50%, -50%)'
    }}></div>
    
    <div style={{
      position: 'absolute',
      left: '50%',
      top: '50%',
      width: '4px',
      height: '4px',
      backgroundColor: 'white',
      borderRadius: '50%',
      transform: 'translate(-50%, -50%)'
    }}></div>
    
    <div style={{
      position: 'absolute',
      top: '2px',
      left: '25%',
      right: '25%',
      height: '60px',
      border: '2px solid white',
      borderTop: 'none'
    }}></div>
    
    <div style={{
      position: 'absolute',
      bottom: '2px',
      left: '25%',
      right: '25%',
      height: '60px',
      border: '2px solid white',
      borderBottom: 'none'
    }}></div>
    
    <div style={{
      position: 'absolute',
      top: '0px',
      left: '0px',
      width: '20px',
      height: '20px',
      border: '2px solid white',
      borderRadius: '0 0 20px 0',
      borderTop: 'none',
      borderLeft: 'none'
    }}></div>
    <div style={{
      position: 'absolute',
      top: '0px',
      right: '0px',
      width: '20px',
      height: '20px',
      border: '2px solid white',
      borderRadius: '0 0 0 20px',
      borderTop: 'none',
      borderRight: 'none'
    }}></div>
    <div style={{
      position: 'absolute',
      bottom: '0px',
      left: '0px',
      width: '20px',
      height: '20px',
      border: '2px solid white',
      borderRadius: '0 20px 0 0',
      borderBottom: 'none',
      borderLeft: 'none'
    }}></div>
    <div style={{
      position: 'absolute',
      bottom: '0px',
      right: '0px',
      width: '20px',
      height: '20px',
      border: '2px solid white',
      borderRadius: '20px 0 0 0',
      borderBottom: 'none',
      borderRight: 'none'
    }}></div>
  </>
);

const renderHalfPitchMarkings = () => (
  <>
    <div style={{
      position: 'absolute',
      top: '2px',
      left: '2px',
      right: '2px',
      bottom: '2px',
      border: '2px solid white',
      borderRadius: '0'
    }}></div>
    
    <div style={{
      position: 'absolute',
      left: '50%',
      top: '0',
      width: '80px',
      height: '40px',
      border: '2px solid white',
      borderRadius: '0 0 40px 40px',
      borderTop: 'none',
      transform: 'translateX(-50%)'
    }}></div>
    
    <div style={{
      position: 'absolute',
      bottom: '2px',
      left: '50%',
      width: '120px',
      height: '60px',
      border: '2px solid white',
      borderBottom: 'none',
      transform: 'translateX(-50%)'
    }}></div>
    
    <div style={{
      position: 'absolute',
      bottom: '2px',
      left: '2px',
      width: '20px',
      height: '20px',
      border: '2px solid white',
      borderRadius: '0 20px 0 0',
      borderBottom: 'none',
      borderLeft: 'none'
    }}></div>
    
    <div style={{
      position: 'absolute',
      bottom: '2px',
      right: '2px',
      width: '20px',
      height: '20px',
      border: '2px solid white',
      borderRadius: '20px 0 0 0',
      borderBottom: 'none',
      borderRight: 'none'
    }}></div>
  </>
);

const renderSimpleRectangle = () => (
  <>
    <div style={{
      position: 'absolute',
      top: '2px',
      left: '2px',
      right: '2px',
      bottom: '2px',
      border: '2px solid white',
      borderRadius: '0'
    }}></div>
  </>
);

// Main PitchRenderer component
const PitchRenderer = ({ 
  pitchSectionConfig, 
  timeSlot, 
  windowSlots,
  filteredAllocations, 
  normalizedPitchId, 
  deletingKeys, 
  canEdit, 
  clearAllocation,
  onUpdateAllocation, // NEW: Handler for updating allocation (e.g., opponent)
  date, // Date parameter is required for correct key format
  teams = [], // NEW: Teams array for name lookup
  referencePoints = null // NEW: Reference points { top, bottom, left, right }
}) => {
  // State for allocation info modal
  const [selectedAllocation, setSelectedAllocation] = useState(null);
  
  // NEW: State for editing opponent
  const [isEditingOpponent, setIsEditingOpponent] = useState(false);
  const [editOpponentValue, setEditOpponentValue] = useState('');
  const [isSavingOpponent, setIsSavingOpponent] = useState(false);
  
  const currentSections = getSectionsForPitch(pitchSectionConfig);
  const gridLayout = getGridLayout(pitchSectionConfig);
  
  // Helper function to get team name from team ID
  const getTeamName = (allocation) => {
    if (!allocation) return 'Unknown Team';
    
    // Check for teamName field first (used by Fixture Scheduler)
    if (allocation.teamName && typeof allocation.teamName === 'string') {
      return allocation.teamName;
    }
    
    // If allocation already has a team name field that doesn't look like an ID, use it
    if (allocation.team && !allocation.team.startsWith('team-')) {
      return allocation.team;
    }
    
    // Otherwise, look up the team by ID
    const teamId = allocation.teamId || allocation.team;
    if (!teamId) {
      return 'Unknown Team';
    }
    
    const teamObj = teams.find(t => t.id === teamId);
    return teamObj?.name || teamId;
  };
  
  // Debug log on mount and whenever allocations change
  useEffect(() => {
    console.log("PitchRenderer: Debug - timeSlot format:", timeSlot);
    console.log("PitchRenderer: Debug - normalizedPitchId:", normalizedPitchId);
    console.log("PitchRenderer: Debug - date:", date);
    console.log("PitchRenderer: Debug - filteredAllocations:", filteredAllocations);
    
    if (Object.keys(filteredAllocations).length > 0) {
      console.log("PitchRenderer: Debug - Keys in filteredAllocations:", Object.keys(filteredAllocations));
      
      // Let's try to understand the key structure
      const sampleKey = Object.keys(filteredAllocations)[0];
      console.log("PitchRenderer: Debug - Sample key structure:", sampleKey, "-> parts:", sampleKey.split("-"));
    }
  }, [timeSlot, normalizedPitchId, filteredAllocations, date]);
  
  // Determine which pitch markings to render based on configuration
  const renderPitchMarkings = () => {
    // No config case
    if (!pitchSectionConfig) {
      return renderFullPitchMarkings();
    }
    
    // Check by section count
    if (pitchSectionConfig.sectionsCount) {
      if (pitchSectionConfig.sectionsCount === 1) return renderSimpleRectangle();
      if (pitchSectionConfig.sectionsCount === 2) return renderSimpleRectangle();
      if (pitchSectionConfig.sectionsCount === 4) return renderHalfPitchMarkings();
      if (pitchSectionConfig.sectionsCount === 8) return renderFullPitchMarkings();
    }
    
    // Check by section layout
    if (pitchSectionConfig.sectionLayout) {
      if (pitchSectionConfig.sectionLayout === 'SMALL') return renderSimpleRectangle();
      if (pitchSectionConfig.sectionLayout === 'QUARTER') return renderSimpleRectangle();
      if (pitchSectionConfig.sectionLayout === 'HALF') return renderHalfPitchMarkings();
      if (pitchSectionConfig.sectionLayout === 'FULL' || pitchSectionConfig.sectionLayout === 'EIGHTH') return renderFullPitchMarkings();
    }
    
    // Default: full pitch
    return renderFullPitchMarkings();
  };

  // Check if we have any reference points to display
  const hasReferencePoints = referencePoints && (
    referencePoints.top || referencePoints.bottom || referencePoints.left || referencePoints.right
  );
  
  // Debug log for reference points
  console.log('[PitchRenderer] Reference points received:', referencePoints);
  console.log('[PitchRenderer] hasReferencePoints:', hasReferencePoints);

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '4px'
    }}>
      {/* Top Reference Point */}
      {hasReferencePoints && referencePoints.top && (
        <div style={{
          fontSize: '10px',
          color: '#6b7280',
          fontWeight: '500',
          textAlign: 'center',
          maxWidth: '280px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          padding: '0 4px'
        }}>
          ▲ {referencePoints.top}
        </div>
      )}
      
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '4px'
      }}>
        {/* Left Reference Point */}
        {hasReferencePoints && referencePoints.left && (
          <div style={{
            fontSize: '10px',
            color: '#6b7280',
            fontWeight: '500',
            writingMode: 'vertical-rl',
            textOrientation: 'mixed',
            transform: 'rotate(180deg)',
            maxHeight: '400px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            padding: '4px 0'
          }}>
            ◀ {referencePoints.left}
          </div>
        )}
        
        {/* Main Pitch Visual */}
        <div className="pitch-visual" style={{
          position: 'relative',
          backgroundColor: '#dcfce7',
          border: '4px solid white',
          borderRadius: '8px',
          padding: '16px',
          width: '280px',
          height: '400px'
        }}>
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: '#bbf7d0',
        borderRadius: '8px',
        overflow: 'hidden'
      }}>
        {renderPitchMarkings()}
      </div>
      
      <div className="pitch-sections" style={{
        position: 'relative',
        display: 'grid',
        gridTemplateColumns: gridLayout.columns,
        gridTemplateRows: gridLayout.rows,
        gap: '4px',
        height: '100%',
        zIndex: 10
      }}>
        {currentSections.map(sec => {
          // Create key using the date and timeSlot
          const key = `${date}-${timeSlot}-${normalizedPitchId}-${sec}`;
          
          // Look up the allocation - check primary slot first, then all window slots
          let allocation = filteredAllocations[key];
          let resolvedKey = key;
          
          // If not found at primary slot, scan across the window slots
          if (!allocation && windowSlots && windowSlots.length > 1) {
            for (const ws of windowSlots) {
              const wsKey = `${date}-${ws}-${normalizedPitchId}-${sec}`;
              if (filteredAllocations[wsKey]) {
                allocation = filteredAllocations[wsKey];
                resolvedKey = wsKey;
                break;
              }
              // Also check partner key format
              const partnerWsKey = `partner-${wsKey}`;
              if (filteredAllocations[partnerWsKey]) {
                allocation = filteredAllocations[partnerWsKey];
                resolvedKey = partnerWsKey;
                break;
              }
            }
          }
          
          // Also check for partner allocation key format at primary slot
          if (!allocation) {
            const partnerKey = `partner-${key}`;
            allocation = filteredAllocations[partnerKey];
            if (allocation) resolvedKey = partnerKey;
          }
          
          const isDeleting = deletingKeys.has(resolvedKey);
          
          // Check if this is a partner allocation
          const isPartnerAllocation = allocation?.isPartnerAllocation || allocation?.isReadOnly;
          
          // Get team name using helper function
          const teamName = getTeamName(allocation);
          
          // Debug log for troubleshooting
          if (allocation) {
            console.log(`PitchRenderer: Found allocation for section ${sec} with key ${key}:`, allocation);
          }
          
          // Determine background color based on allocation type
          let backgroundColor;
          let borderColor;
          let textColor;
          
          if (allocation) {
            if (isPartnerAllocation) {
              // Partner allocations - always grey
              backgroundColor = '#9ca3af90'; // Grey with transparency
              borderColor = '#9ca3af';
              textColor = '#fff';
            } else {
              // Own allocations - use team color
              backgroundColor = (allocation.colour || allocation.color) + '90';
              borderColor = allocation.colour || allocation.color;
              textColor = isLightColor(allocation.colour || allocation.color) ? '#000' : '#fff';
            }
          } else {
            // Empty sections - keep default
            backgroundColor = 'rgba(255,255,255,0.1)';
            borderColor = 'rgba(255,255,255,0.5)';
            textColor = '#374151';
          }
          
          return (
            <div 
              key={sec}
              className="pitch-section"
              style={{
                border: '2px solid rgba(255,255,255,0.5)',
                borderRadius: '4px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: currentSections.length === 1 ? '20px' : currentSections.length === 2 ? '16px' : '12px',
                fontWeight: '500',
                transition: 'all 0.2s',
                position: 'relative',
                padding: currentSections.length === 1 ? '8px' : '2px',
                textAlign: 'center',
                cursor: allocation && !isDeleting ? 'pointer' : 'default',
                backgroundColor: backgroundColor,
                borderColor: borderColor,
                color: textColor,
                opacity: isDeleting ? 0.5 : 1,
                pointerEvents: isDeleting ? 'none' : 'auto'
              }}
              onClick={() => {
                if (!allocation || isDeleting) return;
                
                // Open info modal with allocation details
                setSelectedAllocation({
                  ...allocation,
                  section: sec,
                  allocationKey: resolvedKey,
                  teamName: teamName,
                  isPartnerAllocation: isPartnerAllocation
                });
              }}
              title={allocation 
                ? isPartnerAllocation
                  ? `${teamName} (${allocation.duration}min) - Partner club allocation`
                  : `${teamName} (${allocation.duration}min) - Tap for details`
                : `Section ${sec} - Available`
              }
            >
              {/* Type indicator icon + recurring icon */}
              {allocation && (
                <div style={{
                  position: 'absolute',
                  top: '2px',
                  right: '2px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '2px',
                  zIndex: 20
                }}>
                  {/* Recurring icon */}
                  {allocation.isRecurring && (
                    <div style={{
                      width: currentSections.length === 1 ? '20px' : '14px',
                      height: currentSections.length === 1 ? '20px' : '14px',
                      backgroundColor: '#7c3aed',
                      borderRadius: '2px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                      <svg
                        width={currentSections.length === 1 ? '13px' : '9px'}
                        height={currentSections.length === 1 ? '13px' : '9px'}
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M8 2C5.24 2 3 4.24 3 7H1L3.5 9.5L6 7H4C4 4.79 5.79 3 8 3C10.21 3 12 4.79 12 7C12 9.21 10.21 11 8 11C6.9 11 5.9 10.56 6.18 10.82L5.1 11.9C6.07 12.59 7.49 13 8 13C11.31 13 14 10.31 14 7C14 3.69 11.31 1 8 1V2Z"
                          fill="white"
                        />
                      </svg>
                    </div>
                  )}
                  {/* T / M badge */}
                  <div style={{
                    width: currentSections.length === 1 ? '20px' : '14px',
                    height: currentSections.length === 1 ? '20px' : '14px',
                    backgroundColor: (allocation.type === 'training' || allocation.allocationType === 'training') ? '#3b82f6' : '#ef4444',
                    color: 'white',
                    fontSize: currentSections.length === 1 ? '12px' : '8px',
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '2px',
                  }}>
                    {(allocation.type === 'training' || allocation.allocationType === 'training') ? 'T' : 'M'}
                  </div>
                </div>
              )}
              
              {/* Section label */}
              <div style={{
                fontSize: currentSections.length === 1 ? '24px' : currentSections.length === 2 ? '16px' : '12px',
                opacity: 0.75,
                marginBottom: currentSections.length === 1 ? '8px' : '4px',
                fontWeight: 'bold'
              }}>
                {sec}
              </div>
              
              {/* Team name - NOW RESOLVED */}
              <div style={{
                textAlign: 'center',
                padding: '0 4px',
                fontSize: currentSections.length === 1 ? '18px' : currentSections.length === 2 ? '14px' : '12px',
                lineHeight: 1.2
              }}>
                {allocation ? (isDeleting ? 'Removing...' : teamName) : ''}
              </div>
              
              {/* Duration */}
              {allocation && allocation.isMultiSlot && (
                <div style={{
                  fontSize: currentSections.length === 1 ? '16px' : currentSections.length === 2 ? '14px' : '12px',
                  opacity: 0.6,
                  marginTop: currentSections.length === 1 ? '8px' : '4px'
                }}>
                  {allocation.duration}min
                </div>
              )}
            </div>
          );
        })}
      </div>
      </div>
      {/* End of pitch-visual */}
      
      {/* Right Reference Point */}
      {hasReferencePoints && referencePoints.right && (
        <div style={{
          fontSize: '10px',
          color: '#6b7280',
          fontWeight: '500',
          writingMode: 'vertical-rl',
          textOrientation: 'mixed',
          maxHeight: '400px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          padding: '4px 0'
        }}>
          {referencePoints.right} ▶
        </div>
      )}
      </div>
      {/* End of horizontal flex container */}
      
      {/* Bottom Reference Point */}
      {hasReferencePoints && referencePoints.bottom && (
        <div style={{
          fontSize: '10px',
          color: '#6b7280',
          fontWeight: '500',
          textAlign: 'center',
          maxWidth: '280px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          padding: '0 4px'
        }}>
          {referencePoints.bottom} ▼
        </div>
      )}
      
      {/* Allocation Info Modal */}
      {selectedAllocation && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '16px'
          }}
          onClick={() => { setSelectedAllocation(null); setIsEditingOpponent(false); }}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '16px',
              padding: '24px',
              maxWidth: '320px',
              width: '100%',
              boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
              position: 'relative'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={() => { setSelectedAllocation(null); setIsEditingOpponent(false); }}
              style={{
                position: 'absolute',
                top: '12px',
                right: '12px',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#9ca3af'
              }}
            >
              <X size={20} />
            </button>
            
            {/* Type Badge + Recurring Badge */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
              <div style={{
                display: 'inline-block',
                padding: '4px 12px',
                borderRadius: '20px',
                fontSize: '12px',
                fontWeight: '600',
                backgroundColor: (selectedAllocation.type === 'training' || selectedAllocation.allocationType === 'training') 
                  ? '#dbeafe' 
                  : '#fee2e2',
                color: (selectedAllocation.type === 'training' || selectedAllocation.allocationType === 'training') 
                  ? '#1d4ed8' 
                  : '#dc2626'
              }}>
                {(selectedAllocation.type === 'training' || selectedAllocation.allocationType === 'training') ? 'Training' : 'Match'}
              </div>

              {/* Recurring badge */}
              {selectedAllocation.isRecurring && (
                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '5px',
                  padding: '4px 10px',
                  borderRadius: '20px',
                  fontSize: '12px',
                  fontWeight: '600',
                  backgroundColor: '#ede9fe',
                  color: '#7c3aed'
                }}>
                  <svg width="12" height="12" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M8 2C5.24 2 3 4.24 3 7H1L3.5 9.5L6 7H4C4 4.79 5.79 3 8 3C10.21 3 12 4.79 12 7C12 9.21 10.21 11 8 11C6.9 11 5.9 10.56 6.18 10.82L5.1 11.9C6.07 12.59 7.49 13 8 13C11.31 13 14 10.31 14 7C14 3.69 11.31 1 8 1V2Z"
                      fill="#7c3aed"
                    />
                  </svg>
                  Recurring
                </div>
              )}
            </div>
            
            {/* Team Name */}
            <h3 style={{
              fontSize: '20px',
              fontWeight: '700',
              color: '#1f2937',
              marginBottom: '4px'
            }}>
              {selectedAllocation.teamName || selectedAllocation.team}
            </h3>
            
            {/* Opponent (for matches) - EDITABLE */}
            {(selectedAllocation.type === 'match' || selectedAllocation.allocationType === 'match') && (
              <div style={{ marginBottom: '16px' }}>
                {isEditingOpponent ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '16px', color: '#6b7280' }}>vs</span>
                    <input
                      type="text"
                      value={editOpponentValue}
                      onChange={(e) => setEditOpponentValue(e.target.value)}
                      placeholder="Enter opponent name"
                      autoFocus
                      style={{
                        flex: 1,
                        padding: '6px 10px',
                        fontSize: '14px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        outline: 'none'
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && onUpdateAllocation) {
                          setIsSavingOpponent(true);
                          onUpdateAllocation(selectedAllocation.allocationKey, {
                            fixtureData: { ...(selectedAllocation.fixtureData || {}), awayTeam: editOpponentValue.trim() },
                            opponent: editOpponentValue.trim()
                          }).then(() => {
                            setSelectedAllocation(prev => ({
                              ...prev,
                              fixtureData: { ...(prev.fixtureData || {}), awayTeam: editOpponentValue.trim() },
                              opponent: editOpponentValue.trim()
                            }));
                            setIsEditingOpponent(false);
                          }).catch(err => alert('Failed to save: ' + err.message))
                          .finally(() => setIsSavingOpponent(false));
                        }
                        if (e.key === 'Escape') setIsEditingOpponent(false);
                      }}
                    />
                    <button
                      onClick={() => {
                        if (!onUpdateAllocation) return;
                        setIsSavingOpponent(true);
                        onUpdateAllocation(selectedAllocation.allocationKey, {
                          fixtureData: { ...(selectedAllocation.fixtureData || {}), awayTeam: editOpponentValue.trim() },
                          opponent: editOpponentValue.trim()
                        }).then(() => {
                          setSelectedAllocation(prev => ({
                            ...prev,
                            fixtureData: { ...(prev.fixtureData || {}), awayTeam: editOpponentValue.trim() },
                            opponent: editOpponentValue.trim()
                          }));
                          setIsEditingOpponent(false);
                        }).catch(err => alert('Failed to save: ' + err.message))
                        .finally(() => setIsSavingOpponent(false));
                      }}
                      disabled={isSavingOpponent}
                      style={{
                        padding: '6px',
                        backgroundColor: '#10b981',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: isSavingOpponent ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        opacity: isSavingOpponent ? 0.6 : 1
                      }}
                    >
                      <Check size={16} color="white" />
                    </button>
                    <button
                      onClick={() => setIsEditingOpponent(false)}
                      style={{
                        padding: '6px',
                        backgroundColor: '#ef4444',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <XCircle size={16} color="white" />
                    </button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {(selectedAllocation.fixtureData?.awayTeam || selectedAllocation.opponent) ? (
                      <p style={{ fontSize: '16px', color: '#6b7280', margin: 0 }}>
                        vs {selectedAllocation.fixtureData?.awayTeam || selectedAllocation.opponent}
                      </p>
                    ) : (
                      <p style={{ fontSize: '14px', color: '#9ca3af', fontStyle: 'italic', margin: 0 }}>
                        No opponent set
                      </p>
                    )}
                    {canEdit && !selectedAllocation.isPartnerAllocation && onUpdateAllocation && (
                      <button
                        onClick={() => {
                          setEditOpponentValue(selectedAllocation.fixtureData?.awayTeam || selectedAllocation.opponent || '');
                          setIsEditingOpponent(true);
                        }}
                        style={{
                          padding: '4px 8px',
                          backgroundColor: '#f3f4f6',
                          border: '1px solid #e5e7eb',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          fontSize: '12px',
                          color: '#6b7280'
                        }}
                      >
                        <Pencil size={12} />
                        Edit
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
            
            {/* Details */}
            <div style={{
              backgroundColor: '#f9fafb',
              borderRadius: '10px',
              padding: '16px',
              marginTop: '12px'
            }}>
              {/* Date & Time */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: '12px',
                paddingBottom: '12px',
                borderBottom: '1px solid #e5e7eb'
              }}>
                <div>
                  <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '2px' }}>Date</div>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937' }}>
                    {selectedAllocation.date ? new Date(selectedAllocation.date + 'T00:00:00').toLocaleDateString('en-AU', { 
                      weekday: 'short', 
                      day: 'numeric', 
                      month: 'short' 
                    }) : '-'}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '2px' }}>Time</div>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937' }}>
                    {selectedAllocation.startTime || '-'} - {selectedAllocation.endTime || '-'}
                  </div>
                </div>
              </div>
              
              {/* Pitch & Section */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: selectedAllocation.fixtureData?.round ? '12px' : '0',
                paddingBottom: selectedAllocation.fixtureData?.round ? '12px' : '0',
                borderBottom: selectedAllocation.fixtureData?.round ? '1px solid #e5e7eb' : 'none'
              }}>
                <div>
                  <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '2px' }}>Pitch</div>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937' }}>
                    {selectedAllocation.pitchName || selectedAllocation.pitchId || '-'}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '2px' }}>Section</div>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937' }}>
                    {selectedAllocation.sectionGroup || selectedAllocation.section || '-'}
                  </div>
                </div>
              </div>
              
              {/* Round (for matches) */}
              {selectedAllocation.fixtureData?.round && (
                <div>
                  <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '2px' }}>Round</div>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937' }}>
                    {selectedAllocation.fixtureData.round}
                  </div>
                </div>
              )}
            </div>
            
            {/* Partner allocation notice */}
            {selectedAllocation.isPartnerAllocation && (
              <p style={{
                fontSize: '12px',
                color: '#6b7280',
                marginTop: '12px',
                textAlign: 'center',
                fontStyle: 'italic'
              }}>
                This allocation is from a partner club
              </p>
            )}
            
            {/* Remove button (only if canEdit and not partner) */}
            {canEdit && !selectedAllocation.isPartnerAllocation && (
              <button
                onClick={() => {
                  clearAllocation(selectedAllocation.allocationKey);
                  setSelectedAllocation(null);
                }}
                style={{
                  width: '100%',
                  marginTop: '16px',
                  padding: '12px',
                  backgroundColor: '#fee2e2',
                  color: '#dc2626',
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Remove Allocation
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PitchRenderer;
